import { Section } from '@/components/shared';
import { ROUTE_PATHS } from '@/constants';
import { useAppDispatch, useAppSelector, useAuth, useFetchUser, useGeocoding } from '@/hooks';
import type { IGuestUser, IRegisteredUser } from '@/types';
import { getFullAddress, getFullName, pathJoin, scrollToTop } from '@/utils';
import { Box, Checkbox, Flex, Heading, Text, useBoolean } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CustomCheckboxIcon, ShippingForm } from '../components';
import { StoreInfoBox } from '../components/ShippingStep/StoreSelector/StoreInfoBox';
import { useCouponStore } from '../hooks';
import { updateShipping } from '../store';
import type { IShippingFormFields } from '../types';

const isShippingDataEqualToUserData = (
  user: IGuestUser | IRegisteredUser,
  shipping: IShippingFormFields
): boolean =>
  shipping.recipient === getFullName(user) &&
  shipping.streetAddress === user.streetAddress &&
  shipping.city === user.city &&
  shipping.province === user.province &&
  shipping.countryCode === user.countryCode &&
  shipping.zipCode === user.zipCode;

export const ShippingInfoStep: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  let userInfo = useAppSelector((state) => state.users.user);
  const shippingInfo = useAppSelector((state) => state.quote.shippingInfo);
  const auctionHouse = useAppSelector((state) => state.quote.auctionHouse);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const geocoding = useGeocoding(getFullAddress(auctionHouse));
  const { authUser } = useAuth();
  const { fetch: updateUser, isLoading, user: userInfoDatabase } = useFetchUser();

  const isLogged: boolean = authUser?.isValid() ?? false;

  scrollToTop();

  const { loading: isLoadingCouponStore, store: couponStore } = useCouponStore(
    auctionHouse?.couponCode
  );

  if (!userInfo?.firstName) {
    userInfo = userInfoDatabase;
  }

  const isPreFilled =
    userInfo && shippingInfo && isShippingDataEqualToUserData(userInfo, shippingInfo);

  const [isChecked, setCheckboxState] = useBoolean(isPreFilled);

  const preFillData = useMemo(
    () => ({
      ...shippingInfo,
      streetAddress: userInfo?.streetAddress ?? '',
      city: userInfo?.city ?? '',
      countryCode: userInfo?.countryCode ?? '',
      province: userInfo?.province ?? '',
      zipCode: userInfo?.zipCode ?? '',
      recipient: userInfo ? getFullName(userInfo) : '',
      email: userInfo?.email ?? '',
      phone: userInfo?.phone ?? '',
    }),
    [shippingInfo, userInfo]
  );

  const defaultValues = useMemo(
    () =>
      isChecked ? { store: couponStore, ...preFillData } : { store: couponStore, ...shippingInfo },
    [isChecked, preFillData, shippingInfo, couponStore]
  );

  const centerMapPosition = useMemo(
    () =>
      geocoding
        ? {
            lat: geocoding.geometry.location.lat(),
            lng: geocoding.geometry.location.lng(),
          }
        : undefined,
    [geocoding]
  );

  const handleSubmit = (data: IShippingFormFields): void => {
    dispatch(updateShipping(data));
    const path = pathJoin([`/${ROUTE_PATHS.quote}`, ROUTE_PATHS.quoteSummary]);
    navigate(path);
  };

  const handleBack = (): void => {
    const path = pathJoin([`/${ROUTE_PATHS.quote}`, ROUTE_PATHS.quoteAuctionHouseInfo]);
    navigate(path);
  };

  const shouldBePrefill = !isLogged || userInfoDatabase?.deliveryAddressBook.length === 0;

  return (
    <Section
      containerProps={{ py: 0 }}
      headingProps={{ mt: 0 }}
      id="shipping-section"
      title={t('WIZARD.SHIPPING.STEP_TITLE')}
    >
      <Flex
        alignItems={{ base: 'center', md: 'flex-start' }}
        direction={{ base: 'column', md: 'row' }}
        gap={8}
        justifyContent="center"
        w="full"
      >
        <Box minW="55%" textAlign={{ base: 'center', md: 'left' }}>
          <Heading fontSize={{ base: 'h2.base', md: 'h2.md' }} fontWeight="normal" pb={2}>
            {t('WIZARD.SHIPPING.TITLE')}
          </Heading>
          <Text fontSize="large" mb={8}>
            {t('WIZARD.SHIPPING.CONTENT')}
          </Text>
        </Box>
        {!isLoadingCouponStore && (
          <Flex direction="column" gap={4} minWidth={{ base: 'full', md: 350, xl: 450 }}>
            {couponStore && <StoreInfoBox store={couponStore} />}
            <Heading fontSize="large" fontWeight="bold">
              {t('WIZARD.SHIPPING.FORM_HEADER')}
            </Heading>
            {shouldBePrefill && (
              <Checkbox
                icon={<CustomCheckboxIcon />}
                isChecked={isChecked}
                spacing={4}
                variant="mbe"
                onChange={setCheckboxState.toggle}
              >
                {t('WIZARD.SHIPPING.PRE_FILL_CTA')}
              </Checkbox>
            )}
            <ShippingForm
              addressBook={userInfoDatabase ? userInfoDatabase.deliveryAddressBook : undefined}
              defaultValues={defaultValues}
              initialMapCenter={centerMapPosition}
              isLoading={isLoading}
              isLogged={isLogged}
              isStoreSelectorDisabled={Boolean(couponStore)}
              userAddress={
                userInfoDatabase
                  ? {
                      city: userInfoDatabase.city,
                      countryCode: userInfoDatabase.countryCode,
                      province: userInfoDatabase.province,
                      streetAddress: userInfoDatabase.streetAddress,
                      zipCode: userInfoDatabase.zipCode,
                    }
                  : undefined
              }
              userContactDetails={
                userInfoDatabase
                  ? { email: userInfoDatabase.email, phone: userInfoDatabase.phone }
                  : undefined
              }
              userName={
                userInfoDatabase ? `${userInfoDatabase.firstName} ${userInfoDatabase.lastName}` : ''
              }
              onBack={handleBack}
              onCloseAddressModal={(): void => updateUser()}
              onSubmit={handleSubmit}
            />
          </Flex>
        )}
      </Flex>
    </Section>
  );
};
